import * as React from 'react';

import {
  compose,
  withHandlers,
  setDisplayName,
  onlyUpdateForKeys,
  // @ts-expect-error - TS7016 - Could not find a declaration file for module 'react-recompose'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/react-recompose/dist/Recompose.cjs.js' implicitly has an 'any' type.
} from 'react-recompose';
import {noop} from 'lodash';
import {syncPropsToState} from '@packages/utilities/component';
import {Locator, OverlayViewport} from '@packages/systems/ui/overlay';
import {Dropdown as SelectMenu} from './SelectMenu';
import SelectButton from './SelectButton';
import Hitarea from './Hitarea';

import * as dropdown from './logic/dropdown';
import {emptyObject} from '@packages/utilities/fp/utils';

export {Option, Group} from './Dropdown';

const italic = {fontStyle: 'italic'} as const;

import type {CSSProperties} from '@packages/utilities/css-properties-type';
import {useRegisterLegacyOverlay} from '@packages/systems/spring';
import {FloatingNode} from '@floating-ui/react';

const withDefaultPrevented = (handler: any, open: boolean) => (event: any) => {
  if (open) {
    event.preventDefault();
  }
  /* Replicate default html select behavior of escape key when select is open */
  if (open && event.key === 'Escape') {
    event.stopPropagation();
  }
  handler(event.key);
};

type SelectBaseProps = {
  value?: any;
  disabled?: boolean;
  children: React.ReactNode;
  menuWidth?: number;
  indicator?: boolean;
  preselected?: any;
  hasScrolled?: boolean;
  /**
   * Optional Select button placeholder for when no value is selected.
   * Defaults to "Select..."
   */
  placeholder?: React.ReactNode;
  scrollView: {
    scrollTop: number;
    scrollLeft: number;
  };
  menuOffset?: {
    top: number;
    left: number;
  };
  id?: string;
  ['aria-labelledby']?: string;
  open: boolean;
  dynamic?: boolean;
  onKeyDown?: any;
  onScroll?: any;
  onOpen?: any;
  onClose?: any;
  onPreselect?: any;
  onChange?: any;
  onMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
  onItemMouseDown?: (event: React.MouseEvent<HTMLElement>) => void;
  animated?: boolean;
  style?: CSSProperties;
};

/**
 * @deprecated use `Select` from `@packages/systems/spring` instead
 */
export const SelectBase = ({
  value,
  children,
  preselected,
  hasScrolled,
  placeholder = <span style={italic}>Select...</span>,
  scrollView = {scrollTop: 0, scrollLeft: 0},
  menuOffset = {top: 0, left: 0},
  open = false,
  menuWidth,
  indicator = true,
  onKeyDown = noop,
  onMouseDown = noop,
  onItemMouseDown = noop,
  onScroll = noop,
  onOpen = noop,
  onClose = noop,
  onPreselect = noop,
  onChange = noop,
  dynamic,
  style = emptyObject,
  ...rest
}: SelectBaseProps) => {
  const {nodeId} = useRegisterLegacyOverlay({isOpen: open});

  return (
    <Locator>
      {(ref, anchor) => (
        <>
          <SelectButton
            _ref={ref}
            id={rest['aria-labelledby']}
            {...rest}
            aria-expanded={open}
            aria-haspopup="listbox"
            indicator={indicator}
            onClick={onOpen}
            dynamic={dynamic}
            onKeyDown={withDefaultPrevented(onKeyDown, open)}
            onMouseDown={onMouseDown}
            style={{
              ...style,
            }}
          >
            {dropdown.getSelectedLabelForButton(value, children) ||
              (typeof placeholder === 'string' ? (
                <span style={italic}>{placeholder}</span>
              ) : (
                placeholder
              ))}
          </SelectButton>
          <FloatingNode id={nodeId}>
            {anchor ? (
              <SelectMenu
                aria-labelledby={rest.id || rest['aria-labelledby']}
                width={menuWidth || anchor.width}
                indicator={indicator}
                anchor={anchor}
                open={open}
                value={value}
                menuOffset={menuOffset}
                preselected={preselected}
                hasScrolled={hasScrolled}
                scrollView={scrollView}
                onPreselect={onPreselect}
                onChange={onChange}
                onScroll={onScroll}
                onClose={onClose}
                onRemovePreselection={dropdown.bindNull(onPreselect)}
                onItemMouseDown={onItemMouseDown}
              >
                {children}
              </SelectMenu>
            ) : null}
          </FloatingNode>
        </>
      )}
    </Locator>
  );
};

/* Default state and behavior */

// FIXME:
// eslint-disable-next-line webflow/selector-memoization
const selectPropsTransform = (props: any, state: any) => {
  let {selectState} = state;

  if (props.hasOwnProperty('value') && props.value !== selectState.value) {
    selectState = {
      ...selectState,
      ...dropdown.change(selectState, props.value),
    };
  }

  if (props.hasOwnProperty('open') && props.open !== selectState.open) {
    if (props.open) {
      selectState = {...selectState, ...dropdown.open(selectState)};
    } else {
      selectState = {...selectState, ...dropdown.close(selectState)};
    }
  }

  return {selectState};
};

const syncSelectPropsToState = syncPropsToState(
  {
    selectState: {
      updaterName: 'setSelectState',
      initialValue: ({
        // @ts-expect-error - TS7031 - Binding element 'value' implicitly has an 'any' type.
        value,
        selectState = dropdown.getInitialState({value}),
      }) => selectState,
    },
  },
  selectPropsTransform
);

const withDefaultSelectHandlers = withHandlers({
  onScroll:
    // @ts-expect-error - TS7031 - Binding element 'setSelectState' implicitly has an 'any' type. | TS7031 - Binding element 'selectState' implicitly has an 'any' type. | TS7031 - Binding element 'children' implicitly has an 'any' type.

    ({setSelectState, selectState, children}) =>
      (value: any) => {
        setSelectState(dropdown.scroll({...selectState, children}, value));
      },
  onOpen:
    // @ts-expect-error - TS7031 - Binding element 'setSelectState' implicitly has an 'any' type. | TS7031 - Binding element 'handleOpen' implicitly has an 'any' type.

    ({setSelectState, handleOpen}) =>
      () => {
        handleOpen ? handleOpen(dropdown.open) : setSelectState(dropdown.open);
      },
  onClose:
    // @ts-expect-error - TS7031 - Binding element 'setSelectState' implicitly has an 'any' type. | TS7031 - Binding element 'handleClose' implicitly has an 'any' type.

    ({setSelectState, handleClose}) =>
      () => {
        handleClose
          ? handleClose(dropdown.close)
          : setSelectState(dropdown.close);
      },
  onPreselect:
    // @ts-expect-error - TS7031 - Binding element 'handlePreselect' implicitly has an 'any' type. | TS7031 - Binding element 'setSelectState' implicitly has an 'any' type. | TS7031 - Binding element 'selectState' implicitly has an 'any' type. | TS7031 - Binding element 'children' implicitly has an 'any' type.

    ({handlePreselect, setSelectState, selectState, children}) =>
      (value: any) => {
        handlePreselect
          ? handlePreselect(value)
          : setSelectState(
              dropdown.preselect({...selectState, children}, value)
            );
      },
  onChange:
    // @ts-expect-error - TS7031 - Binding element 'setSelectState' implicitly has an 'any' type. | TS7031 - Binding element 'selectState' implicitly has an 'any' type. | TS7031 - Binding element 'children' implicitly has an 'any' type. | TS7031 - Binding element 'handleChange' implicitly has an 'any' type.

    ({setSelectState, selectState, children, handleChange}) =>
      (value: any) => {
        /**
         * If we're handling the change directly, there is a chance that
         * it won't trigger a re-render so the select won't close. This
         * forces the select to close every time handleChange fires.
         */
        if (handleChange) {
          setSelectState(dropdown.close, () => handleChange(value));
        } else {
          setSelectState(dropdown.change({...selectState, children}, value));
        }
      },
  onKeyDown:
    // @ts-expect-error - TS7031 - Binding element 'setSelectState' implicitly has an 'any' type. | TS7031 - Binding element 'selectState' implicitly has an 'any' type. | TS7031 - Binding element 'children' implicitly has an 'any' type. | TS7031 - Binding element 'handleChange' implicitly has an 'any' type.

    ({setSelectState, selectState, children, handleChange}) =>
      (key: any) => {
        const newState = dropdown.handleKeyDown(
          {...selectState, children},
          key
        );

        if (newState.value !== selectState.value) {
          handleChange(newState.value);
        } else {
          setSelectState(newState);
        }
      },
});

const SelectWithHandlers = withDefaultSelectHandlers(
  ({
    // @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type.
    children,
    // @ts-expect-error - TS7031 - Binding element 'menuWidth' implicitly has an 'any' type.
    menuWidth,
    // @ts-expect-error - TS7031 - Binding element 'indicator' implicitly has an 'any' type.
    indicator,
    // @ts-expect-error - TS7031 - Binding element 'placeholder' implicitly has an 'any' type.
    placeholder,
    // @ts-expect-error - TS7031 - Binding element 'disabled' implicitly has an 'any' type.
    disabled,
    // @ts-expect-error - TS7031 - Binding element 'dynamic' implicitly has an 'any' type.
    dynamic,
    // @ts-expect-error - TS7031 - Binding element 'selectState' implicitly has an 'any' type.
    selectState,
    // @ts-expect-error - TS7031 - Binding element 'onScroll' implicitly has an 'any' type.
    onScroll,
    // @ts-expect-error - TS7031 - Binding element 'onOpen' implicitly has an 'any' type.
    onOpen,
    // @ts-expect-error - TS7031 - Binding element 'onClose' implicitly has an 'any' type.
    onClose,
    // @ts-expect-error - TS7031 - Binding element 'onPreselect' implicitly has an 'any' type.
    onPreselect,
    // @ts-expect-error - TS7031 - Binding element 'onChange' implicitly has an 'any' type.
    onChange,
    // @ts-expect-error - TS7031 - Binding element 'onKeyDown' implicitly has an 'any' type.
    onKeyDown,
    // @ts-expect-error - TS7031 - Binding element 'onMouseDown' implicitly has an 'any' type.
    onMouseDown,
    // @ts-expect-error - TS7031 - Binding element 'onItemMouseDown' implicitly has an 'any' type.
    onItemMouseDown,
    ...rest
  }) => (
    <OverlayViewport>
      <Hitarea>
        <SelectBase
          {...selectState}
          {...rest}
          menuWidth={menuWidth}
          indicator={indicator}
          placeholder={placeholder}
          disabled={disabled}
          dynamic={dynamic}
          onScroll={onScroll}
          onOpen={onOpen}
          onClose={onClose}
          onPreselect={onPreselect}
          onChange={onChange}
          onKeyDown={onKeyDown}
          onMouseDown={onMouseDown}
          onItemMouseDown={onItemMouseDown}
        >
          {children}
        </SelectBase>
      </Hitarea>
    </OverlayViewport>
  )
);

const enhanceSelect = compose(
  syncSelectPropsToState,
  setDisplayName('Select'),
  onlyUpdateForKeys([
    'selectState',
    'value',
    'disabled',
    'children',
    'menuWidth',
    'indicator',
    'placeholder',
    'open',
    'handleOpen',
    'handleClose',
    'handleChange',
  ])
);

export type SelectProps = {
  value?: any;
  disabled?: boolean;
  dynamic?: boolean;
  menuWidth?: number;
  indicator?: boolean;
  /**
   * Optional Select button placeholder for when no value is selected.
   * Defaults to "Select..."
   */
  placeholder?: React.ReactNode;
  open?: boolean;
  handleOpen?: any;
  handleClose?: any;
  handleChange?: (arg1?: any) => void | null | undefined;
  animated?: boolean;
  style?: CSSProperties;
  // FIXME: Should this be something like Group | Option?
  children?: React.ReactNode;
};

/**
 * @deprecated use `Select` from `@packages/systems/spring` instead
 */
export const Select: React.ComponentType<SelectProps> =
  enhanceSelect(SelectWithHandlers);
